import { useSyncExternalStore } from 'react';
import { t } from './i18n.js';
import { currentInstallWay, installSnapshot, promptInstall, subscribeInstall, type InstallWay } from './install.js';
import { Dialog } from './pages/rundown-dialogs.js';

/** The steps for the platform the reader is on; the dialog names the others too, for a tablet handed over. */
const STEPS: Record<Exclude<InstallWay, 'installed' | 'prompt'>, { title: () => string; steps: Array<() => string> }> = {
  ios: { title: () => t('installIosTitle'), steps: [() => t('installIos1'), () => t('installIos2'), () => t('installIos3')] },
  android: { title: () => t('installAndroidTitle'), steps: [() => t('installAndroid1'), () => t('installAndroid2'), () => t('installAndroid3')] },
  desktop: { title: () => t('installDesktopTitle'), steps: [() => t('installDesktop1'), () => t('installDesktop2'), () => t('installDesktop3')] },
};
const ORDER: Array<keyof typeof STEPS> = ['desktop', 'android', 'ios'];

// One dialog for the whole frame, opened from either link (top links or the menu drawer): it lives outside the
// drawer, so closing the drawer does not take it away and it does not inherit the drawer's colours.
type Open = 'steps' | 'dismissed' | null;
let open: Open = null;
const watchers = new Set<() => void>();
const setOpen = (v: Open) => {
  open = v;
  watchers.forEach((w) => w());
};
const subscribeOpen = (w: () => void) => {
  watchers.add(w);
  return () => watchers.delete(w);
};
const openSnapshot = () => open;

/**
 * "Εγκατάσταση" (ADR 093): the browser's install prompt when it offered one, else the steps for this device's own
 * menu. Gone once AirPost runs as an installed app. `onOpen` lets the drawer close itself.
 */
export function InstallLink({ className, onOpen }: { className?: string; onOpen?: () => void }) {
  useSyncExternalStore(subscribeInstall, installSnapshot, installSnapshot);
  if (currentInstallWay() === 'installed') return null;

  const click = async () => {
    onOpen?.();
    if (currentInstallWay() === 'prompt') {
      const outcome = await promptInstall();
      if (outcome !== 'accepted') setOpen('dismissed');
      return;
    }
    setOpen('steps');
  };

  return (
    <a
      href="#install"
      className={className}
      title={t('installHint')}
      onClick={(e) => {
        e.preventDefault();
        void click();
      }}
    >
      {t('install')}
    </a>
  );
}

/** The install dialog: rendered once by the frame, outside the drawer. */
export function InstallDialog() {
  const shown = useSyncExternalStore(subscribeOpen, openSnapshot, openSnapshot);
  useSyncExternalStore(subscribeInstall, installSnapshot, installSnapshot);
  if (!shown) return null;
  const way = currentInstallWay();
  const mine: keyof typeof STEPS = way === 'ios' || way === 'android' ? way : 'desktop';
  return (
    <Dialog title={t('installTitle')} onClose={() => setOpen(null)}>
      <div className="install-dialog">
        <p>{shown === 'dismissed' ? t('installDismissed') : t('installIntro')}</p>
        {!window.isSecureContext && <p className="banner warn">{t('installNeedsHttps')}</p>}
        {[mine, ...ORDER.filter((k) => k !== mine)].map((k, i) => (
          <section key={k} className={i === 0 ? 'mine' : 'other'}>
            <h3>{STEPS[k].title()}</h3>
            <ol>
              {STEPS[k].steps.map((s, n) => (
                <li key={n}>{s()}</li>
              ))}
            </ol>
          </section>
        ))}
        <p className="muted">{t('installAfter')}</p>
      </div>
    </Dialog>
  );
}
